import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #selectionGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selectionGeneration += 1;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Price and payment terms are pending-deny until exact server approval is returned.
    const generation = ++this.#selectionGeneration;
    this.hidden = true;
    const currentInstallments = this.querySelector('.installments');
    const currentInstallmentsInput = currentInstallments?.querySelector('input[name="id"]');
    if (currentInstallmentsInput instanceof HTMLInputElement) currentInstallmentsInput.value = '';

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration) return;
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Find the exact replacement price first so authority is scoped to this block's product boundary.
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        const responseCarrier = newProductPrice?.closest('product-component[data-launch-allowed="true"]');
        const approvedVariantId = responseCarrier?.getAttribute('data-approved-variant-id')?.trim() ?? '';
        const newInstallmentsId =
          newProductPrice?.querySelector('.installments input[name="id"]')?.getAttribute('value')?.trim() ?? '';
        const serverAllowed =
          approvedVariantId !== '' &&
          detail.resource?.available === true &&
          String(detail.resource.id ?? '') === approvedVariantId &&
          (newInstallmentsId === '' || newInstallmentsId === approvedVariantId);

        if (!serverAllowed) {
          currentInstallments?.remove();
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        if (!newProductPrice || generation !== this.#selectionGeneration) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const newInstallments = newProductPrice.querySelector('.installments');
        if (newInstallments && currentInstallments) {
          currentInstallments.replaceWith(newInstallments);
        } else if (newInstallments && !currentInstallments) {
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newInstallments.cloneNode(true)));
        } else {
          currentInstallments?.remove();
        }

        this.hidden = false;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
